import {AfterViewInit, Component, Input, OnDestroy} from '@angular/core';
import { Chart } from 'angular-highcharts';
import {TransactionService} from '../../../services/transaction.service';
import {DisplayFieldPipe} from '../../pipes/displayField.pipe';
import * as _ from 'lodash';
import {Router} from '@angular/router';
import * as moment from 'moment-timezone';
import {HighchartsService} from '../../../services/highcharts.service';
import {ChartDrilldownEvent} from 'highcharts';
import {DatesService} from '../../../services/dates.service';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs/Subscription';

@Component({
    selector: 'app-transactions-details',
    templateUrl: './transactions-details.component.html',
    styleUrls: ['./transactions-details.component.scss'],
    providers: [HighchartsService, DatesService]
})
export class TransactionsDetailsComponent implements AfterViewInit, OnDestroy {

    @Input() chartOnly = false;
    @Input() zoom = 4;
    @Input() preloadData: any = null;

    private preloadSubscription: Subscription;
    private seriesSubscription: Subscription;
    private detailsSubscription: Subscription;

    periods: Array<any> = [{
        value: HighchartsService.DRILLDOWN_DAY,
        text: 'Daily'
    }, {
        value: HighchartsService.DRILLDOWN_WEEK,
        text: 'Weekly'
    }, {
        value: HighchartsService.DRILLDOWN_MONTH,
        text: 'Monthly'
    }, {
        value: HighchartsService.DRILLDOWN_YEAR,
        text: 'Yearly'
    }];

    drilldownLevel = 1;
    aggrigatedBy: { [key: number]: number; } = {};

    companies: Array<any> = [];

    minDate:   Date = new Date(2000, 1, 1);
    startYear: Date = new Date(2010, 1, 1);

    filters: any = {
        aggrigatedBy: this.zoom,
        CO1_ID: [],
        ED1_MODIFIED_ON_FROM: null,
        ED1_MODIFIED_ON_TO: null
    };

    chart: Chart;

    constructor(
        private transactionService: TransactionService,
        private highchartsService: HighchartsService,
        private datesService: DatesService,
        private router: Router,
        private title: Title
    ) {
        this.aggrigatedBy[1] = HighchartsService.DRILLDOWN_YEAR;
        this.aggrigatedBy[2] = HighchartsService.DRILLDOWN_MONTH;
        this.aggrigatedBy[3] = HighchartsService.DRILLDOWN_WEEK;
        this.aggrigatedBy[4] = HighchartsService.DRILLDOWN_DAY;
    }

    ngAfterViewInit() {
        if (!this.chartOnly) {
            this.title.setTitle('EDI - Transactions');
        }
        this.filters['aggrigatedBy'] = this.zoom;
        if (this.preloadData === null) {
            this.preloadSubscription = this.transactionService.preloadData().subscribe(res => {
                this.handlePreloadData(res);
            }, () => {
                // failed to load data
            });
        } else {
            this.handlePreloadData(this.preloadData);
        }
    }

    ngOnDestroy() {
        if (this.preloadSubscription) {
            this.preloadSubscription.unsubscribe();
        }
        if (this.seriesSubscription) {
            this.seriesSubscription.unsubscribe();
        }
        if (this.detailsSubscription) {
            this.detailsSubscription.unsubscribe();
        }
    }

    handlePreloadData(res) {
        const me = this;
        this.companies = res.companies;
        this.chart = new Chart({
            chart: {
                type: 'column',
                events: {
                    drillup: function (e: ChartDrilldownEvent ) {

                        me.drilldownLevel--;

                        const rotation = e.seriesOptions.data.length > 10 ? -45 : 0
                        me.highchartsService.updateXAxis( me.chart, me.aggrigatedBy[me.drilldownLevel - 1],  rotation );
                    },
                    drilldown: function (e) {
                        if (!e.seriesOptions) {

                            const chart = this;
                            const serie = me.highchartsService.getSerieByPoint(me.chart, e.point);

                            if (serie) {
                                const params = {
                                    aggrigatedBy:  me.aggrigatedBy[me.drilldownLevel],
                                };

                                if (serie['CO1_ID']) {
                                    params['CO1_ID'] = serie['CO1_ID'];
                                    params['groupBy'] = 'CO1_ID';
                                }

                                if ( me.drilldownLevel > 1 ) {
                                    const startDate  = moment.tz(new Date(e.point.x), 'UTC');
                                    let endDate    = new Date( Date.UTC(startDate.year(), 11, 31) );
                                    if ( me.aggrigatedBy[me.drilldownLevel] == HighchartsService.DRILLDOWN_MONTH ) {
                                        endDate    = new Date( Date.UTC(startDate.year(), 11, 31) );
                                    } else if (me.aggrigatedBy[me.drilldownLevel] == HighchartsService.DRILLDOWN_WEEK) {
                                        endDate    = new Date( Date.UTC(startDate.year(), startDate.month(), startDate.daysInMonth()) );
                                    } else if ( me.aggrigatedBy[me.drilldownLevel] == HighchartsService.DRILLDOWN_DAY ) {
                                        endDate    = moment(startDate).add(6, 'days');
                                    }

                                    params['ED1_MODIFIED_ON_FROM'] = me.datesService.format(
                                        startDate.toString(), '00:00:00', 'UTC'
                                    );
                                    params['ED1_MODIFIED_ON_TO']   = me.datesService.format(
                                        moment(endDate).tz('UTC').toString(), '23:59:59', 'UTC'
                                    );
                                } else {

                                    if (me.filters['ED1_MODIFIED_ON_FROM']) {
                                        params['ED1_MODIFIED_ON_FROM'] = me.datesService.format(me.filters['ED1_MODIFIED_ON_FROM']);
                                    }
                                    if (me.filters['ED1_MODIFIED_ON_TO']) {
                                        params['ED1_MODIFIED_ON_TO'] = me.datesService.format(
                                            me.filters['ED1_MODIFIED_ON_TO'], '23:59:59'
                                        );
                                    }
                                }

                                // Show the loading label
                                chart.showLoading('Loading ...');
                                me.seriesSubscription = me.transactionService.getSeries([params]).subscribe( data => {
                                    chart.hideLoading();

                                    data.serie.forEach( serieData => {
                                        const drilldownData = serieData.data[0].data.map( item => {
                                            const result = {};
                                            result['name']       = serie.name;
                                            result['x']          = item[0];
                                            result['y']          = item[1];
                                            if ( me.aggrigatedBy[me.drilldownLevel] > HighchartsService.DRILLDOWN_DAY) {
                                                result['drilldown'] = [];
                                                result['drilldownType']  = 'column';
                                            }
                                            if (serie['CO1_ID']) {
                                                result['CO1_ID']  = serie['CO1_ID'];
                                                params['groupBy'] = 'CO1_ID';
                                            }
                                            return result;
                                        });

                                        chart.addSeriesAsDrilldown(e.point, {
                                            type: serie.drilldownType,
                                            name: serie.name,
                                            data: drilldownData,
                                            tooltip: {
                                                headerFormat: '<span style=\'font-size: 10px\'>{point.x:%Y, %b %d}</span><br/>',
                                                pointFormat: '{series.name}: <b>{point.y:,.0f}</b>'
                                            },
                                            dataLabels: {
                                                format: '{point.y:,.0f}'
                                            }
                                        });

                                        const rotation = drilldownData.length > 10 ? -45 : 0
                                        me.highchartsService.updateXAxis( me.chart, me.aggrigatedBy[me.drilldownLevel],  rotation );

                                        me.drilldownLevel ++;
                                    });
                                });
                            }
                        }
                    }
                }
            },

            title: {
                text: 'EDI Transactions'
            },
            xAxis: {
                title: {
                    text: 'Timeline'
                },
                type: 'datetime',
            },
            yAxis: {
                min: 0,
                title: {
                    text: 'Number of transactions'
                }
            },
            plotOptions: {
                column: {
                    depth: 25
                }
            },
            exporting: {
                enabled: false
            }
        });
        this.onSubmit();
    }

    loadChartData(params: any = null) {
        this.detailsSubscription = this.transactionService.getDetailsReport(params).subscribe(data => {

            this.highchartsService.destroyDrillDown( this.chart);
            this.highchartsService.removeAllSeries( this.chart );

            const me = this;
            const displayFieldPipe = new DisplayFieldPipe();

            data.forEach(function (serie) {
                const name = serie.name ? serie.name :
                    displayFieldPipe.transform(serie.CO1_ID, 'CO1_ID', 'CO1_NAME', me.companies);

                const rotation = serie.data.length > 10 ? -45 : 0
                me.highchartsService.updateXAxis( me.chart, params['aggrigatedBy'],  rotation);

                me.chart.addSerie({
                    name: name,
                    data: serie.data.map( item => {
                        const result = {};
                        result['name']          = name;
                        result['x']             = item[0];
                        result['y']             = item[1];
                        result['drilldown']     = [];
                        result['drilldownType'] = 'column';
                        result['CO1_ID']        = serie.CO1_ID;
                        return result;
                    })
                });

                if (me.filters['aggrigatedBy'] == HighchartsService.DRILLDOWN_YEAR) {
                    me.drilldownLevel = 2;
                } else if (me.filters['aggrigatedBy'] == HighchartsService.DRILLDOWN_MONTH) {
                    me.drilldownLevel = 3;
                } else if (me.filters['aggrigatedBy'] == HighchartsService.DRILLDOWN_WEEK) {
                    me.drilldownLevel = 4;
                } else if (me.filters['aggrigatedBy'] == HighchartsService.DRILLDOWN_DAY) {
                    me.drilldownLevel = 5;
                }
            });

        });
    }

    onSubmit() {

        const filters = _.clone(this.filters);

        filters['CO1_ID[]'] = filters['CO1_ID'];
        delete filters['CO1_ID'];

        if (filters['ED1_MODIFIED_ON_FROM']) {
            filters['ED1_MODIFIED_ON_FROM'] = this.datesService.format(filters['ED1_MODIFIED_ON_FROM']);
        }
        if (filters['ED1_MODIFIED_ON_TO']) {
            filters['ED1_MODIFIED_ON_TO'] = this.datesService.format(filters['ED1_MODIFIED_ON_TO'], '23:59:59');
        }

        this.loadChartData(filters);
    }

    onCancel(): void {
        this.router.navigate(['/transactions']);
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/transactions/transactions-details/transactions-details.component.ts